import type { OccupationInfo } from '@ps/web-core-store';
import { InfoTooltip, StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { UpdateAccountDetailsAction } from '@swf/core/types/Account';

import { PEPHIOOptions } from '../PEPHIOSelection/PEPHIOSelection';

import {
  TXTCLI_Confirm,
  TXTWCL_According_to_Ontario_regulatio_ELL,
  TXTWCL_Company_Address,
  TXTWCL_Company_Phone,
  TXTWCL_Edit_Details,
  TXTWCL_Full_name_of_the_PEP_HIO,
  TXTWCL_NOX_I_am_not_a_PEP_HIOX_and_I__ELL,
  TXTWCL_Occupation,
  TXTWCL_Organization_Institution_name,
  TXTWCL_Source_of_funds_being_deposite_ELL,
  TXTWCL_Source_of_wealth_used_for_fund_ELL,
  TXTWCL_YESX_I_am_a_PEP_HIOX,
  TXTWCL_YESX_I_am_related_to_associate_ELL,
  TXTWCL_Your_relationship
} from './OccupationSummary.messages.json';
import styles from './OccupationSummary.scss';

const occupationFields = [
  { tag: TXTWCL_Occupation, fieldName: 'occupation' },
  { tag: TXTWCL_Company_Phone, fieldName: 'companyPhone' },
  { tag: TXTWCL_Company_Address, fieldName: 'companyAddress' },
  { tag: TXTWCL_Organization_Institution_name, fieldName: 'companyName' },
  { tag: TXTWCL_Source_of_funds_being_deposite_ELL, fieldName: 'sourceOfFunds' },
  { tag: TXTWCL_Source_of_wealth_used_for_fund_ELL, fieldName: 'sourceOfWealth' },
  { tag: TXTWCL_Full_name_of_the_PEP_HIO, fieldName: 'pepHioName' },
  { tag: TXTWCL_Your_relationship, fieldName: 'relationshipToPepHio' }
];

const PEPHIOTags = {
  [PEPHIOOptions.PEPOrHIO]: TXTWCL_YESX_I_am_a_PEP_HIOX,
  [PEPHIOOptions.PEPOrHIORelated]: TXTWCL_YESX_I_am_related_to_associate_ELL,
  [PEPHIOOptions.NotPEPOrHIO]: TXTWCL_NOX_I_am_not_a_PEP_HIOX_and_I__ELL
};

interface Props {
  notification?: boolean;
  onEdit: () => void;
}

const OccupationSummary: React.FC<Props> = ({ notification, onEdit }) => {
  const occupationInfo = useSelector(select.account.getOccupationInfo);
  const {
    modal,
    account: { confirmAccountDetails }
  } = useDispatch<Dispatch>();

  const handleConfirm = () =>
    confirmAccountDetails({
      action: UpdateAccountDetailsAction.Confirm
    }).then(() => modal.close());

  return (
    <>
      <br />
      {occupationInfo?.pepHioSelection &&
        occupationInfo?.pepHioSelection !== PEPHIOOptions.NotSelected && (
          <StarsInfo type="info" className={styles.tooltipWrapper}>
            <Translation
              tagName="b"
              {...PEPHIOTags[occupationInfo?.pepHioSelection as keyof typeof PEPHIOTags]}
            />
            <InfoTooltip
              iconClassName={styles.infoTooltip}
              content={[{ ...TXTWCL_According_to_Ontario_regulatio_ELL }]}
            />
          </StarsInfo>
        )}
      <br />
      {occupationFields.map(
        (field, index) =>
          occupationInfo &&
          occupationInfo[field.fieldName as keyof OccupationInfo] && (
            <div className={styles.container} key={index}>
              <b className={styles.info}>
                <Translation {...field.tag} />
              </b>
              <span>{occupationInfo[field.fieldName as keyof OccupationInfo]}</span>
            </div>
          )
      )}
      {notification && (
        <StarsButton
          block
          onClick={handleConfirm}
          translation={TXTCLI_Confirm}
          className={sharedStyles.submit}
        />
      )}
      <StarsButton
        block
        onClick={onEdit}
        translation={TXTWCL_Edit_Details}
        variant={notification ? 'secondary' : 'primary'}
        className={sharedStyles.submit}
      />
    </>
  );
};

export default OccupationSummary;
